/*
A CVSS v2 score calculator.
Based on:
 - https://nvd.nist.gov/vuln-metrics/cvss/v2-calculator
 - https://www.first.org/cvss/v2/cvss-v2-guide.pdf
 - https://www.first.org/cvss/calculator/3.1
Emits:
 @update

Pass in individual metric (i.e. AV, AC, Au, C, I, A, E, RL, RC, CR, IR, AR, CDP, TD)
or pass in the whole CVSS vector.
*/
import VueApexCharts from 'vue-apexcharts';
// eslint-disable-next-line import/no-extraneous-dependencies
import { debounce } from 'lodash';
import { CVSSv2 } from '../../../assets/libs/3rd-party/cvss2';

const RADAR_V2_LABELS = [
    'Attack Surface Exposure',
    'Ease of Exploit (Technical)',
    'Ease of Exploit (Auth Barrier)',
    'Risk to Data Confidentiality',
    'Risk to Data Integrity',
    'Risk to System Availability',
    'Exploit Attainability',
    'Difficulty of Remediation',
    'Confidence in Vulnerability Report',
    'Collateral Damage Potential',
    'Attack Surface Extent',
];

const RADIAL_V2_MAPPING = {
    AV: {
        N: 5,
        A: 3,
        L: 2,
    },
    AC: {
        L: 5,
        M: 3,
        H: 2,
    },
    Au: {
        N: 5,
        S: 3,
        M: 2,
    },
    C: {
        H: {
            C: 5,
            P: 3,
            N: 0,
        },
        M: {
            C: 4,
            P: 2,
            N: 0,
        },
        L: {
            C: 3,
            P: 1,
            N: 0,
        },
        ND: {
            C: 5,
            P: 3,
            N: 0,
        },
    },
    I: {
        H: {
            C: 5,
            P: 3,
            N: 0,
        },
        M: {
            C: 4,
            P: 2,
            N: 0,
        },
        L: {
            C: 3,
            P: 1,
            N: 0,
        },
        ND: {
            C: 5,
            P: 3,
            N: 0,
        },
    },
    A: {
        H: {
            C: 5,
            P: 3,
            N: 0,
        },
        M: {
            C: 4,
            P: 2,
            N: 0,
        },
        L: {
            C: 3,
            P: 1,
            N: 0,
        },
        ND: {
            C: 5,
            P: 3,
            N: 0,
        },
    },
    E: {
        H: 5,
        F: 4,
        POC: 3,
        U: 2,
        ND: 3,
    },
    RL: {
        U: 5,
        W: 4,
        TF: 3,
        OF: 2,
        ND: 3,
    },
    RC: {
        C: 5,
        UR: 3,
        UC: 2,
        ND: 3,
    },
    CDP: {
        H: 5,
        MH: 4,
        LM: 3,
        L: 2,
        N: 1,
        ND: 3,
    },
    TD: {
        H: 5,
        M: 3,
        L: 2,
        N: 0,
        ND: 3,
    },
};

const RADAR_V2_LABEL_NAMES = {
    5: 'Very High',
    4: 'High',
    3: 'Medium',
    2: 'Low',
    1: 'Very Low',
    0: 'None',
};

export const CVSSV2Calculator = {
    props: {
        cvssVector: {
            type: String,
            default: null,
        },
        cvssAV: {
            type: String,
            default: null,
        },
        cvssAC: {
            type: String,
            default: null,
        },
        cvssAu: {
            type: String,
            default: null,
        },
        cvssC: {
            type: String,
            default: null,
        },
        cvssI: {
            type: String,
            default: null,
        },
        cvssA: {
            type: String,
            default: null,
        },
        cvssE: {
            type: String,
            default: 'ND',
        },
        cvssRL: {
            type: String,
            default: 'ND',
        },
        cvssRC: {
            type: String,
            default: 'ND',
        },
        cvssCR: {
            type: String,
            default: 'ND',
        },
        cvssIR: {
            type: String,
            default: 'ND',
        },
        cvssAR: {
            type: String,
            default: 'ND',
        },
        cvssCDP: {
            type: String,
            default: 'ND',
        },
        cvssTD: {
            type: String,
            default: 'ND',
        },
        readOnly: {
            type: Boolean,
            default: false,
        },
    },
    components: {
        apexchart: VueApexCharts,
    },
    data() {
        const chartBarSeries = [
            {
                name: 'CVSS V2',
                data: [0, 0, 0],
            },
        ];
        const chartBarHeight = 125;
        const chartBarOptions = {
            chart: {
                id: 'cvss-v3-score-chart',
                height: chartBarHeight,
                type: 'bar',
            },
            stroke: {
                width: 2,
            },
            fill: {
                opacity: 0.8,
                colors: ['#428bca'],
            },
            plotOptions: {
                bar: {
                    borderRadius: 4,
                    horizontal: true,
                },
            },
            dataLabels: {
                enabled: false,
            },
            yaxis: {
                min: 0,
                max: 10,
                tickAmount: 10,
            },
            xaxis: {
                categories: ['Base Score', 'Temporal Score', 'Environmental Score'],
            },
            series: [
                {
                    name: 'Scores',
                    data: [0, 0, 0],
                },
            ],
        };
        const chartType = 'radar';
        const chartHeight = 250;
        const chartSeries = [
            {
                name: 'CVSS V2',
                data: RADAR_V2_LABELS.map(() => 0),
            },
        ];
        const chartOptions = {
            chart: {
                id: 'cvss-v3-score-chart',
                height: chartHeight,
                type: chartType,
            },
            stroke: {
                width: 2,
            },
            theme: {
                palette: 'palette2',
            },
            plotOptions: {
                radar: {
                    size: 100,
                    polygons: {
                        strokeColors: '#e9e9e9',
                        fill: {
                            colors: ['#f8f8f8', '#fff'],
                        },
                    },
                },
            },
            dataLabels: {
                enabled: false,
            },
            tooltip: {
                y: {
                    formatter: (e) => RADAR_V2_LABEL_NAMES[e],
                },
            },
            colors: ['#FF4560'],
            markers: {
                size: 4,
                colors: ['#fff'],
                strokeColor: '#FF4560',
                strokeWidth: 2,
            },
            yaxis: {
                min: 0,
                max: 5,
                tickAmount: 5,
                labels: {
                    formatter: () => '', // RADAR_V3_LABEL_NAMES[e],
                },
            },
            xaxis: {
                categories: RADAR_V2_LABELS,
            },
            series: chartSeries,
        };

        return {
            badgeClass: 'badge badge-cvss',
            score: null,
            baseScore: null,
            temporalScore: null,
            environmentalScore: null,
            vector: this.cvssVector ?? '-',
            AV: this.cvssAV,
            AC: this.cvssAC,
            Au: this.cvssAu,
            C: this.cvssC,
            I: this.cvssI,
            A: this.cvssA,
            E: this.cvssE,
            RL: this.cvssRL,
            RC: this.cvssRC,
            CDP: this.cvssCDP,
            TD: this.cvssTD,
            CR: this.cvssCR,
            IR: this.cvssIR,
            AR: this.cvssAR,
            chartOptions,
            chartSeries,
            chartType,
            chartHeight,
            chartBarSeries,
            chartBarHeight,
            chartBarOptions,
        };
    },
    computed: {},
    methods: {
        CVSSColour(score) {
            if (score === 0) {
                return 'cvss-zero';
            }
            if (score < 4.0) {
                return 'cvss-low';
            }
            if (score < 7.0) {
                return 'cvss-medium';
            }
            if (score < 9.0) {
                return 'cvss-high';
            }
            return 'cvss-critical';
        },
        calculateRadarChartScores() {
            return Object.keys(RADIAL_V2_MAPPING).map((e) => {
                if (e === 'C' || e === 'I' || e === 'A') {
                    const k = `${e}R`;
                    return RADIAL_V2_MAPPING[e][this[k]][this[e]];
                }
                return RADIAL_V2_MAPPING[e][this[e]];
            });
        },
        checkBase() {
            // returns whether all base values are selected
            return [this.AC, this.AV, this.Au, this.C, this.I, this.A].some((e) => !e);
        },
        updateChartBarSeries(series) {
            this.chartBarSeries = series;
            this.chartBarOptions = {
                ...this.chartBarOptions,
                series,
            };
        },
        updateChartSeries(series) {
            this.chartSeries = series;
            this.chartOptions = {
                ...this.chartOptions,
                series,
            };
        },
        updateScoreVector(
            baseMetricScore,
            temporalMetricScore,
            environmentalMetricScore,
            vectorString,
        ) {
            this.vector = vectorString;
            this.baseScore = baseMetricScore;
            this.temporalScore = temporalMetricScore;
            this.environmentalScore = environmentalMetricScore;
            this.updateChartBarSeries([
                {
                    name: 'CVSS V2',
                    data: [this.baseScore, this.temporalScore, this.environmentalScore],
                },
            ]);
            this.updateChartSeries([
                {
                    name: 'CVSS V2',
                    data: this.calculateRadarChartScores(),
                },
            ]);
            this.$emit('update', {
                vector: this.vector,
                baseScore: this.baseScore,
                temporalScore: this.temporalScore,
                environmentalScore: this.environmentalScore,
            });
        },
        calculateScore() {
            try {
                if (this.checkBase()) {
                    // some of the base selects are missing - no update possible
                    return;
                }
                const {
                    success,
                    vectorString,
                    baseMetricScore,
                    temporalMetricScore,
                    environmentalMetricScore,
                } = CVSSv2.calculateCVSSFromMetrics(
                    this.AV,
                    this.AC,
                    this.Au,
                    this.C,
                    this.I,
                    this.A,
                    this.E,
                    this.RL,
                    this.RC,
                    this.CDP,
                    this.TD,
                    this.CR,
                    this.IR,
                    this.AR,
                );
                if (success) {
                    this.updateScoreVector(
                        baseMetricScore,
                        temporalMetricScore,
                        environmentalMetricScore,
                        vectorString,
                    );
                }
            } catch (e) {
                //
            }
        },
        updateMetrics(AV, AC, Au, C, I, A, E, RL, RC, CDP, TD, CR, IR, AR) {
            if (AV) this.AV = AV;
            if (AC) this.AC = AC;
            if (Au) this.Au = Au;
            if (C) this.C = C;
            if (I) this.I = I;
            if (A) this.A = A;
            if (E) this.E = E;
            if (RL) this.RL = RL;
            if (RC) this.RC = RC;
            if (CDP) this.CDP = CDP;
            if (TD) this.TD = TD;
            if (CR) this.CR = CR;
            if (IR) this.IR = IR;
            if (AR) this.AR = AR;
        },
        updateScoreFromVector(vector) {
            if (vector) {
                try {
                    const {
                        success,
                        AV,
                        AC,
                        Au,
                        C,
                        I,
                        A,
                        E,
                        RL,
                        RC,
                        CDP,
                        TD,
                        CR,
                        IR,
                        AR,
                        vectorString,
                        baseMetricScore,
                        temporalMetricScore,
                        environmentalMetricScore,
                    } = CVSSv2.calculateCVSSFromVector(vector);
                    if (success) {
                        this.updateMetrics(AV, AC, Au, C, I, A, E, RL, RC, CDP, TD, CR, IR, AR);
                        this.updateScoreVector(
                            baseMetricScore,
                            temporalMetricScore,
                            environmentalMetricScore,
                            vectorString,
                        );
                    }
                } catch (e) {
                    //
                }
            }
        },
        handleVectorChange: debounce(function handler(event) {
            try {
                const vector = event?.target?.value;
                if (vector) {
                    this.updateScoreFromVector(vector);
                }
            } catch (e) {
                //
            }
        }, 50),
    },
    watch: {},
    beforeDestroy() {
        if (this.$refs['cvss-v2-chart-1'] && this.$refs['cvss-v2-chart-1'].chart) {
            this.$refs['cvss-v2-chart-1'].chart.destroy();
        }
        if (this.$refs['cvss-v2-chart-2'] && this.$refs['cvss-v2-chart-2'].chart) {
            this.$refs['cvss-v2-chart-2'].chart.destroy();
        }
    },
    mounted() {
        if (this.vector && this.vector !== '-') {
            this.updateScoreFromVector(this.vector);
        }
    },
    template: `
  <!--

  Copyright (c) 2019, FIRST.ORG, INC.
  All rights reserved.

  Redistribution and use in source and binary forms, with or without modification, are permitted provided that the
  following conditions are met:
  1. Redistributions of source code must retain the above copyright notice, this list of conditions and the following
     disclaimer.
  2. Redistributions in binary form must reproduce the above copyright notice, this list of conditions and the
     following disclaimer in the documentation and/or other materials provided with the distribution.
  3. Neither the name of the copyright holder nor the names of its contributors may be used to endorse or promote
     products derived from this software without specific prior written permission.

  THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES,
  INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE
  DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT HOLDER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL,
  SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR
  SERVICES; LOSS OF USE, DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY,
  WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE
  OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
-->
    <div class="cvss-calculator">

        <div v-bind:style="{ height: chartHeight*1.25, paddingBottom: '0.25rem' }">
            <apexchart ref="cvss-v2-chart-2" :type="chartType" :options="chartOptions" :series="chartSeries"
                :height="chartHeight" />
        </div>

        <div v-bind:style="{ height: chartBarHeight*1.25, paddingBottom: '0.25rem' }">
            <apexchart ref="cvss-v2-chart-1" type="bar" :options="chartBarOptions" :series="chartBarSeries"
                :height="chartBarHeight" />
        </div>

        <div class="cvss-score-rating">
            <p v-if="!baseScore" class="alert alert-dark p-2 m-2">Select values for all base metrics to generate score</p>
            <span id="cvss-base-metric-core"></span>
            <span id="cvss-base-severity"></span>
        </div>

        <dl class="cvss-fieldset cvss-score-rating">
            <dt class="col-sm-3">Base Score: </dt>
            <dd class="col-sm-9">
                <span id="CVSS_V2_base_score" v-bind:class="[badgeClass, CVSSColour(baseScore)]">{{ baseScore ?? "-" }}</span>
            </dd>

            <dt class="col-sm-3">Temporal Score: </dt>
            <dd class="col-sm-9">
                <span id="CVSS_V2_temporal_score" v-bind:class="[badgeClass, CVSSColour(temporalScore)]">{{ temporalScore ?? "-" }}</span>
            </dd>

            <dt class="col-sm-3">Environmental Score: </dt>
            <dd class="col-sm-9">
                <span id="CVSS_V2_env_score" v-bind:class="[badgeClass, CVSSColour(environmentalScore)]">{{ environmentalScore ?? "-" }}</span>
            </dd>

            <dt class="col-sm-3">Vector: </dt>
            <dd class="col-sm-9">
                <input id="CVSS_V2_vector" :disabled="true" class="form-control w-100" type="text" v-model="vector" @change="handleVectorChange" />
            </dd>
        </dl>


        <fieldset id="baseMetricGroup" class="cvss-fieldset">
            <legend class="font-weight-bold" id="baseMetricGroup_Legend"
                title="The Base Metric group represents the intrinsic  characteristics of a vulnerability that are constant over time and across user environments. Determine the vulnerable component and score Attack Vector, Attack Complexity, Privileges Required and User Interaction relative to this.">
                Base Score</legend>

            <div class="col-lg-6">

                <div class="cvss-metric">
                    <div id="AV_Heading"
                        title="This metric reflects how the vulnerability is exploited. The more remote an attacker can be to attack a host, the greater the vulnerability score.">
                        Access Vector (AV)</div>
                    <input :disabled="readOnly" @change="calculateScore" v-model="AV" name="CVSS_V2_AV" value="N" id="AV_N" type="radio" /><label
                        for="AV_N" id="AV_N_Label"
                        title="A vulnerability exploitable with network access means the vulnerable software is bound to the network stack and the attacker does not require local network access or local access. Such a vulnerability is often termed “remotely exploitable”. An example of a network attack is an RPC buffer overflow.">Network
                        (N)</label>
                    <input :disabled="readOnly" @change="calculateScore" v-model="AV" name="CVSS_V2_AV" value="A" id="AV_A" type="radio" /><label
                        for="AV_A" id="AV_A_Label"
                        title="A vulnerability exploitable with adjacent network access requires the attacker to have access to either the broadcast or collision domain of the vulnerable software. Examples of local networks include local IP subnet, Bluetooth, IEEE 802.11, and local Ethernet segment.">Adjacent
                        Network (A)</label>
                    <input :disabled="readOnly" @change="calculateScore" v-model="AV" name="CVSS_V2_AV" value="L" id="AV_L" type="radio" /><label
                        for="AV_L"
                        id="AV_L_Label"
                        title="A vulnerability exploitable with only local access requires the attacker to have either physical access to the vulnerable system or a local (shell) account. Examples of locally exploitable vulnerabilities are peripheral attacks such as Firewire/USB DMA attacks, and local privilege escalations (e.g., sudo).">Local
                        (L)</label>
                </div>

                <div class="cvss-metric">
                    <div id="AC_Heading"
                        title="This metric measures the complexity of the attack required to exploit the vulnerability once an attacker has gained access to the target system. For example, consider a buffer overflow in an Internet service: once the target system is located, the attacker can launch an exploit at will.">
                        Access Complexity (AC)</div>
                    <input :disabled="readOnly" @change="calculateScore" v-model="AC" name="CVSS_V2_AC" value="L" id="AC_L" type="radio" /><label
                        for="AC_L" id="AC_L_Label"
                        title="Specialized access conditions or extenuating circumstances do not exist. The following are examples: • The affected product typically requires access to a wide range of systems and users, possibly anonymous and untrusted (e.g., Internet-facing web or mail server). • The affected configuration is default or ubiquitous. • The attack can be performed manually and requires little skill or additional information gathering. • The “race condition” is a lazy one (i.e., it is technically a race but easily winnable)">Low
                        (L)</label>
                    <input :disabled="readOnly" @change="calculateScore" v-model="AC" name="CVSS_V2_AC" value="M" id="AC_M" type="radio" /><label
                        for="AC_M" id="AC_M_Label"
                        title="The access conditions are somewhat specialized; the following are examples: • The attacking party is limited to a group of systems or users at some level of authorization, possibly untrusted. • Some information must be gathered before a successful attack can be launched. • The affected configuration is non-default, and is not commonly configured (e.g., a vulnerability present when a server performs user account authentication via a specific scheme, but not present for another authentication scheme). Common Vulnerability Scoring System (v2) - 8 - • The attack requires a small amount of social engineering that might occasionally fool cautious users (e.g., phishing attacks that modify a web browser’s status bar to show a false link, having to be on someone’s “buddy” list before sending an IM exploit)">Medium
                        (M)</label>
                    <input :disabled="readOnly" @change="calculateScore" v-model="AC" name="CVSS_V2_AC" value="H" id="AC_H" type="radio" /><label
                        for="AC_H" id="AC_H_Label"
                        title="Specialized access conditions exist. For example: • In most configurations, the attacking party must already have elevated privileges or spoof additional systems in addition to the attacking system (e.g., DNS hijacking). • The attack depends on social engineering methods that would be easily detected by knowledgeable people. For example, the victim must perform several suspicious or atypical actions. • The vulnerable configuration is seen very rarely in practice. • If a race condition exists, the window is very narrow.">High
                        (H)</label>
                </div>

                <div class="cvss-metric">
                    <div id="AU_Heading"
                        title="This metric measures the number of times an attacker must authenticate to a target in order to exploit a vulnerability. This metric does not gauge the strength or complexity of the authentication process, only that an attacker is required to provide credentials before an exploit may occur.">
                        Authentication (Au)</div>
                    <input :disabled="readOnly" @change="calculateScore" v-model="Au" name="CVSS_V2_AU" value="N" id="AU_H" type="radio" /><label
                        for="AU_H" id="AU_H_Label"
                        title="Authentication is not required to access and exploit the vulnerability.">None (N)</label>
                    <input :disabled="readOnly" @change="calculateScore" v-model="Au" name="CVSS_V2_AU" value="S" id="AU_L" type="radio" /><label
                        for="AU_L" id="AU_L_Label"
                        title="One instance of authentication is required to access and exploit the vulnerability">Single
                        (S)</label>
                    <input :disabled="readOnly" @change="calculateScore" v-model="Au" name="CVSS_V2_AU" value="M" id="AU_N" type="radio" /><label
                        for="AU_N" id="AU_N_Label"
                        title="Exploiting the vulnerability requires that the attacker authenticate two or more times, even if the same credentials are used each time. An example is an attacker authenticating to an operating system in addition to providing credentials to access an application hosted on that system.">Multiple
                        (M)</label>
                </div>

            </div>


            <div class="col-lg-6">

                <div class="cvss-metric">
                    <div id="C_Heading"
                        title="This metric measures the impact to the confidentiality of the information resources managed by a software component due to a successfully exploited vulnerability. Confidentiality refers to limiting information access and disclosure to only authorized users, as well as preventing access by, or disclosure to, unauthorized ones.">
                        Confidentiality (C)</div>
                    <input :disabled="readOnly" @change="calculateScore" v-model="C" name="CVSS_V2_C" value="N" id="C_N" type="radio" /><label for="C_N"
                        id="C_N_Label" title="There is no impact to the confidentiality of the system.">None (N)</label>
                    <input :disabled="readOnly" @change="calculateScore" v-model="C" name="CVSS_V2_C" value="P" id="C_P" type="radio" /><label for="C_P"
                        id="C_P_Label"
                        title="There is considerable informational disclosure. Access to some system files is possible, but the attacker does not have control over what is obtained, or the scope of the loss is constrained. An example is a vulnerability that divulges only certain tables in a database.">Partial
                        (P)</label>
                    <input :disabled="readOnly" @change="calculateScore" v-model="C" name="CVSS_V2_C" value="C" id="C_C" type="radio" /><label for="C_C"
                        id="C_C_Label"
                        title="There is total information disclosure, resulting in all system files being revealed. The attacker is able to read all of the system's data (memory, files, etc.)">Complete
                        (C)</label>
                </div>

                <div class="cvss-metric">
                    <div id="I_Heading"
                        title="This metric measures the impact to integrity of a successfully exploited vulnerability. Integrity refers to the trustworthiness and veracity of information.">
                        Integrity (I)</div>
                    <input :disabled="readOnly" @change="calculateScore" v-model="I" name="CVSS_V2_I" value="N" id="I_N" type="radio" /><label for="I_N"
                        id="I_N_Label" title="There is no impact to the integrity of the system">None (N)</label>
                    <input :disabled="readOnly" @change="calculateScore" v-model="I" name="CVSS_V2_I" value="P" id="I_P" type="radio" /><label for="I_P"
                        id="I_P_Label"
                        title="Modification of some system files or information is possible, but the attacker does not have control over what can be modified, or the scope of what the attacker can affect is limited. For example, system or application files may be overwritten or modified, but either the attacker has no control over which files are affected or the attacker can modify files within only a limited context or scope.">Partial
                        (P)</label>
                    <input :disabled="readOnly" @change="calculateScore" v-model="I" name="CVSS_V2_I" value="C" id="I_C" type="radio" /><label for="I_C"
                        id="I_C_Label"
                        title="There is a total compromise of system integrity. There is a complete loss of system protection, resulting in the entire system being compromised. The attacker is able to modify any files on the target system">Complete
                        (C)</label>
                </div>

                <div class="cvss-metric">
                    <div id="A_Heading"
                        title="This metric measures the impact to availability of a successfully exploited vulnerability. Availability refers to the accessibility of information resources. Attacks that consume network bandwidth, processor cycles, or disk space all impact the availability of a system.">
                        Availability (A)</div>
                    <input :disabled="readOnly" @change="calculateScore" v-model="A" name="CVSS_V2_A" value="N" id="A_N" type="radio" /><label for="A_N"
                        id="A_N_Label" title="There is no impact to availability within the impacted component.">None
                        (N)</label>
                    <input :disabled="readOnly" @change="calculateScore" v-model="A" name="CVSS_V2_A" value="P" id="A_P" type="radio" /><label for="A_P"
                        id="A_P_Label"
                        title="There is reduced performance or interruptions in resource availability. An example is a network-based flood attack that permits a limited number of successful connections to an Internet service.">Partial
                        (P)</label>
                    <input :disabled="readOnly" @change="calculateScore" v-model="A" name="CVSS_V2_A" value="C" id="A_C" type="radio" /><label for="A_C"
                        id="A_C_Label"
                        title="There is a total shutdown of the affected resource. The attacker can render the resource completely unavailable">Complete
                        (C)</label>
                </div>

            </div>

        </fieldset>

        <fieldset id="temporalMetricGroup" class="cvss-fieldset">
            <legend id="temporalMetricGroup_Legend"
                title="The threat posed by a vulnerability may change over time. Three such factors that CVSS captures are: confirmation of the technical details of a vulnerability, the remediation status of the vulnerability, and the availability of exploit code or techniques. Since temporal metrics are optional they each include a metric value that has no effect on the score. This value is used when the user feels the particular metric does not apply and wishes to “skip over” it.">
                Temporal Score</legend>

            <div class="col-lg-6">

                <div class="cvss-metric">
                    <div id="E_Heading"
                        title="This metric measures the current state of exploit techniques or code availability. Public availability of easy-to-use exploit code increases the number of potential attackers by including those who are unskilled, thereby increasing the severity of the vulnerability">
                        Exploitability (E)</div>
                    <input :disabled="readOnly" @change="calculateScore" v-model="E" name="CVSS_V2_E" value="U" id="E_U" type="radio" ><label for="E_U"
                        id="E_U_Label" title="No exploit code is available, or an exploit is entirely theoretical.">Unproven
                        (U)</label>
                    <input :disabled="readOnly" @change="calculateScore" v-model="E" name="CVSS_V2_E" value="POC" id="E_P" type="radio" /><label
                        for="E_P" id="E_P_Label"
                        title="Proof-of-concept exploit code or an attack demonstration that is not practical for most systems is available. The code or technique is not functional in all situations and may require substantial modification by a skilled attacker.">Proof-of-Concept
                        (POC)</label>
                    <input :disabled="readOnly" @change="calculateScore" v-model="E" name="CVSS_V2_E" value="F" id="E_F" type="radio" /><label for="E_F"
                        id="E_F_Label"
                        title="Functional exploit code is available. The code works in most situations where the vulnerability exists.">Functional
                        (F)</label>
                    <input :disabled="readOnly" @change="calculateScore" v-model="E" name="CVSS_V2_E" value="H" id="E_H" type="radio" /><label for="E_H"
                        id="E_H_Label"
                        title="Either the vulnerability is exploitable by functional mobile autonomous code, or no exploit is required (manual trigger) and details are widely available. The code works in every situation, or is actively being delivered via a mobile autonomous agent (such as a worm or virus).">High
                        (H)</label>
                    <input :disabled="readOnly" @change="calculateScore" v-model="E" name="CVSS_V2_E" value="ND" id="E_ND" checked="" type="radio"/><label for="E_ND" id="E_ND_Label"
                        title="Assigning this value to the metric will not influence the score. It is a signal to the equation to skip this metric">Not Defined (ND)</label>
                </div>

                <div class="cvss-metric">
                    <div id="RL_Heading"
                        title="The remediation level of a vulnerability is an important factor for prioritization. The typical vulnerability is unpatched when initially published. Workarounds or hotfixes may offer interim remediation until an official patch or upgrade is issued. Each of these respective stages adjusts the temporal score downwards, reflecting the decreasing urgency as remediation becomes final.">
                        Remediation Level (RL)</div>
                    <input :disabled="readOnly" @change="calculateScore" v-model="RL" name="CVSS_V2_RL" value="OF" id="RL_OF" type="radio" /><label
                        for="RL_OF" id="RL_OF_Label"
                        title="A complete vendor solution is available. Either the vendor has issued an official patch, or an upgrade is available.">Official
                        Fix (OF)</label>
                    <input :disabled="readOnly" @change="calculateScore" v-model="RL" name="CVSS_V2_RL" value="TF" id="RL_TF" type="radio" /><label
                        for="RL_TF" id="RL_TF_Label"
                        title="There is an official but temporary fix available. This includes instances where the vendor issues a temporary hotfix, tool, or workaround.">Temporary
                    Fix (TF)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="RL" name="CVSS_V2_RL" value="W" id="RL_W" type="radio" /><label
                    for="RL_W" id="RL_W_Label"
                    title="There is an unofficial, non-vendor solution available. In some cases, users of the affected technology will create a patch of their own or provide steps to work around or otherwise mitigate the vulnerability.">Workaround
                    (W)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="RL" name="CVSS_V2_RL" value="U" id="RL_U" type="radio" /><label
                    for="RL_U" id="RL_U_Label"
                    title="There is either no solution available or it is impossible to apply.">Unavailable (U)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="RL" name="CVSS_V2_RL" value="ND" id="RL_ND" checked=""
                    type="radio" /><label for="RL_ND" id="RL_ND_Label"
                    title="Assigning this value to the metric will not influence the score. It is a signal to the equation to skip this metric.">Not
                    Defined (ND)</label>
            </div>

            <div class="cvss-metric">
                <div id="RC_Heading"
                    title="This metric measures the degree of confidence in the existence of the vulnerability and the credibility of the known technical details. Sometimes, only the existence of vulnerabilities are publicized, but without specific details. The vulnerability may later be corroborated and then confirmed through acknowledgement by the author or vendor of the affected technology. The urgency of a vulnerability is higher when a vulnerability is known to exist with certainty. This metric also suggests the level of technical knowledge available to would-be attackers. ">
                    Report Confidence (RC)</div>
                <input :disabled="readOnly" @change="calculateScore" v-model="RC" name="CVSS_V2_RC" value="UC" id="RC_UC" type="radio" /><label
                    for="RC_UC" id="RC_UC_Label"
                    title="There are multiple non-official sources, possibly including independent security companies or research organizations. At this point there may be conflicting technical details or some other lingering ambiguity">Unconfirmed
                    (UC)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="RC" name="CVSS_V2_RC" value="UR" id="RC_UR" type="radio" /><label
                    for="RC_UR" id="RC_UR_Label"
                    title="Significant details are published, but researchers either do not have full confidence in the root cause, or do not have access to source code to fully confirm all of the interactions that may lead to the result. Reasonable confidence exists, however, that the bug is reproducible and at least one impact is able to be verified (Proof-of-concept exploits may provide this). An example is a detailed write-up of research into a vulnerability with an explanation (possibly obfuscated or 'left as an exercise to the reader') that gives assurances on how to reproduce the results.">Uncorroborated
                    (UR)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="RC" name="CVSS_V2_RC" value="C" id="RC_C" type="radio" /><label
                    for="RC_C" id="RC_C_Label"
                    title="The vulnerability has been acknowledged by the vendor or author of the affected technology. The vulnerability may also be “Confirmed” when its existence is confirmed from an external event such as publication of functional or proof-of- concept exploit code or widespread exploitation">Confirmed
                    (C)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="RC" name="CVSS_V2_RC" value="ND" id="RC_ND" checked=""
                    type="radio" /><label for="RC_ND" id="RC_ND_Label"
                    title="Assigning this value to the metric will not influence the score. It is a signal to the equation to skip this metric.">Not
                    Defined (ND)</label>
            </div>

        </div>


    </fieldset>

    <fieldset id="environmentalMetricGroup" class="cvss-fieldset">
        <legend id="environmentalMetricGroup_Legend"
            title="These metrics capture the characteristics of a vulnerability that are associated with a user’s IT environment. Since environmental metrics are optional they each include a metric value that has no effect on the score. This value is used when the user feels the particular metric does not apply and wishes to “skip over” it.">
            Environmental Score</legend>

        <div class="col-lg-6">

            <div class="cvss-metric">
                <div id="CDP_Heading"
                    title="This metric measures the potential for loss of life or physical assets through damage or theft of property or equipment. The metric may also measure economic loss of productivity or revenue.">
                    Collateral Damage Potential (CDP)</div>
                <input :disabled="readOnly" @change="calculateScore" v-model="CDP" name="CVSS_V2_CDP" value="N" id="CDP_N" checked=""
                    type="radio" /><label for="CDP_N" id="CDP_N_Label"
                    title="There is no potential for loss of life, physical assets, productivity or revenue.">None
                    N)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="CDP" name="CVSS_V2_CDP" value="L" id="CDP_L" type="radio" /><label
                    for="CDP_L" id="CDP_L_Label"
                    title="A successful exploit of this vulnerability may result in slight physical or property damage. Or, there may be a slight loss of revenue or productivity to the organization.">Low
                    (L)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="CDP" name="CVSS_V2_CDP" value="LM" id="CDP_LM" type="radio" /><label
                    for="CDP_LM" id="CDP_LM_Label"
                    title="A successful exploit of this vulnerability may result in moderate physical or property damage. Or, there may be a moderate loss of revenue or productivity to the organization.">Low-Medium
                    (LM)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="CDP" name="CVSS_V2_CDP" value="MH" id="CDP_MH" type="radio" /><label
                    for="CDP_MH" id="CDP_MH_Label"
                    title="A successful exploit of this vulnerability may result in significant physical or property damage or loss. Or, there may be a significant loss of revenue or productivity.">Medium-High
                    (MH)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="CDP" name="CVSS_V2_CDP" value="H" id="CDP_H" type="radio" /><label
                    for="CDP_H" id="CDP_H_Label"
                    title="A successful exploit of this vulnerability may result in catastrophic physical or property damage and loss. Or, there may be a catastrophic loss of revenue or productivity.">High
                    (H)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="CDP" name="CVSS_V2_CDP" value="ND" id="CDP_ND" checked=""
                    type="radio" /><label for="CDP_ND" id="CDP_ND_Label"
                    title="Assigning this value to the metric will not influence the score. It is a signal to the equation to skip this metric.">Not
                    Defined (ND)</label>
            </div>

            <div class="cvss-metric">
                <div id="TD_Heading"
                    title="This metric measures the proportion of vulnerable systems. It is meant as an environment-specific indicator in order to approximate the percentage of systems that could be affected by the vulnerability.">
                    Target Distribution (TD)</div>
                <input :disabled="readOnly" @change="calculateScore" v-model="TD" name="CVSS_V2_TD" value="N" id="TD_N" type="radio" /><label
                    for="TD_N" id="TD_N_Label"
                    title="No target systems exist, or targets are so highly specialized that they only exist in a laboratory setting. Effectively 0% of the environment is at risk.">None
                    (N)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="TD" name="CVSS_V2_TD" value="L" id="TD_L" type="radio" /><label
                    for="TD_L" id="TD_L_Label"
                    title="Targets exist inside the environment, but on a small scale. Between 1% - 25% of the total environment is at risk.">Low
                    (L)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="TD" name="CVSS_V2_TD" value="M" id="TD_M" type="radio" /><label
                    for="TD_M" id="TD_M_Label"
                    title="Targets exist inside the environment, but on a medium scale. Between 26% - 75% of the total environment is at risk.">Medium
                    (M)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="TD" name="CVSS_V2_TD" value="H" id="TD_H" type="radio" /><label
                    for="TD_H" id="TD_H_Label"
                    title="Targets exist inside the environment, but on a medium scale. Between 26% - 75% of the total environment is at risk.">High
                    (H)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="TD" name="CVSS_V2_TD" value="ND" id="TD_ND" checked=""
                    type="radio" /><label for="TD_ND" id="TD_ND_Label"
                    title="Assigning this value to the metric will not influence the score. It is a signal to the equation to skip this metric">Not
                    Defined (ND)</label>
            </div>

        </div>

        <div class="col-lg-6">

            <div class="cvss-metric">
                <div id="CR_Heading"
                    title="These metrics enable the analyst to customize the CVSS score depending on the importance of the affected IT asset to a user’s organization, measured in terms of confidentiality, integrity, and availability, That is, if an IT asset supports a business function for which availability is most important, the analyst can assign a greater value to availability, relative to confidentiality and integrity.">
                    Confidentiality Requirements (CR)</div>
                <input :disabled="readOnly" @change="calculateScore" v-model="CR" name="CVSS_V2_CR" value="L" id="CR_L" type="radio" /><label
                    for="CR_L" id="CR_L_Label"
                    title="Loss of confidentiality is likely to have only a limited adverse effect on the organization or individuals associated with the organization (e.g., employees, customers).">Low (L)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="CR" name="CVSS_V2_CR" value="M" id="CR_M" type="radio" /><label
                    for="CR_M" id="CR_M_Label"
                    title="Loss of confidentiality is likely to have a serious advers effect on the organization or individuals associated with the organization (e.g. employees, customers).">Medium
                    (M)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="CR" name="CVSS_V2_CR" value="H" id="CR_H" type="radio" /><label
                    for="CR_H" id="CR_H_Label"
                    title="Loss of confidentiality is likely to have a catastrophic adverse effect on the organization or individuals associated with the organization (e.g., employees, customers).">High (H)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="CR" name="CVSS_V2_CR" value="ND" id="CR_ND" checked=""
                    type="radio" /><label for="CR_ND" id="CR_ND_Label"
                    title="Assigning this value to the metric will not influence the score. It is a signal to the equation to skip this metric.">Not
                    Defined (ND)</label>
            </div>

            <div class="cvss-metric">
                <div id="IR_Heading"
                    title="These metrics enable the analyst to customize the CVSS score depending on the importance of the affected IT asset to a user’s organization, measured in terms of confidentiality, integrity, and availability, That is, if an IT asset supports a business function for which availability is most important, the analyst can assign a greater value to availability, relative to confidentiality and integrity.">
                    Integrity Requirements (IR)</div>
                <input :disabled="readOnly" @change="calculateScore" v-model="IR" name="CVSS_V2_IR" value="L" id="IR_L" type="radio" /><label
                    for="IR_L" id="IR_L_Label"
                    title="Loss of integrity is likely to have only a limited adverse effect on the organization or individuals associated with the organization (e.g., employees, customers).">Low (L)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="IR" name="CVSS_V2_IR" value="M" id="IR_M" type="radio" /><label
                    for="IR_M" id="IR_M_Label"
                    title="Loss of integrity is likely to have a serious advers effect on the organization or individuals associated with the organization (e.g. employees, customers).">Medium
                    (M)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="IR" name="CVSS_V2_IR" value="H" id="IR_H" type="radio" /><label
                    for="IR_H" id="IR_H_Label"
                    title="Loss of integrity is likely to have a catastrophic adverse effect on the organization or individuals associated with the organization (e.g., employees, customers).">High (H)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="IR" name="CVSS_V2_IR" value="ND" id="IR_ND" checked=""
                    type="radio" /><label for="IR_ND" id="IR_ND_Label"
                    title="Assigning this value to the metric will not influence the score. It is a signal to the equation to skip this metric.">Not
                    Defined (ND)</label>
            </div>

            <div class="cvss-metric">
                <div id="AR_Heading"
                    title="These metrics enable the analyst to customize the CVSS score depending on the importance of the affected IT asset to a user’s organization, measured in terms of confidentiality, integrity, and availability, That is, if an IT asset supports a business function for which availability is most important, the analyst can assign a greater value to availability, relative to confidentiality and integrity.">
                    Availability Requirements (AR)</div>
                <input :disabled="readOnly" @change="calculateScore" v-model="AR" name="CVSS_V2_AR" value="L" id="AR_L" type="radio" /><label
                    for="AR_L" id="AR_L_Label"
                    title="Loss of availability is likely to have only a limited adverse effect on the organization or individuals associated with the organization (e.g., employees, customers).">Low (L)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="AR" name="CVSS_V2_AR" value="M" id="AR_M" type="radio" /><label
                    for="AR_M" id="AR_M_Label"
                    title="Loss of availability is likely to have a serious advers effect on the organization or individuals associated with the organization (e.g. employees, customers).">Medium
                    (M)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="AR" name="CVSS_V2_AR" value="H" id="AR_H" type="radio" /><label
                    for="AR_H" id="AR_H_Label"
                    title="Loss of availability is likely to have a catastrophic adverse effect on the organization or individuals associated with the organization (e.g., employees, customers).">High (H)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="AR" name="CVSS_V2_AR" value="ND" id="AR_ND" checked=""
                    type="radio" /><label for="AR_ND" id="AR_ND_Label"
                    title="Assigning this value to the metric will not influence the score. It is a signal to the equation to skip this metric.">Not
                    Defined (ND)</label>
            </div>
        </div>

    </fieldset>
</div>
`,
};
